import {createBrowserRouter, Navigate, Outlet} from "react-router-dom";
import React, {Suspense, ReactNode} from "react";
import SuspenseLoader from "./components/feedBack/SuspenseLoader";
import lazyWithRetry from "./helpers/lazyWithRetry";

// ─── Layouts (statiques car légers et structurels) ───
import DefaultLayout from "./components/layouts/DefaultLayout";
import GuestLayout from "./components/layouts/GuestLayout";
import PublicLayout from "./components/layouts/PublicLayout";
import CustomerSpaceLayout from "./components/layouts/CustomerSpaceLayout";
import RequireCustomer from "./components/router/RequireCustomer";
import RequireStaff from "./components/router/RequireStaff";
import RequirePermission from "./components/router/RequirePermission";
import SmartRedirect from "./components/router/SmartRedirect";
import {authLoader} from "./components/router/authLoader";
import NotFound from "./pages/NotFound";

import CompaniesModuleGuard from "./pages/companies/CompaniesModuleGuard";
import GroupContextGate from "./pages/companies/GroupContextGate";
import HotelEventsModuleGuard from "./pages/hotel-events/HotelEventsModuleGuard";
import { PageHeaderDefaultsProvider } from "./components/layouts/PageHeaderDefaultsContext";
import { LegacyCampaignTabRedirect, RedirectKeepingQuery } from "./pages/super-admin/crm/prospection/LegacyRedirects";
import { PROSPECTING_PATHS } from "./pages/super-admin/crm/prospection/prospectingPaths";

// ─── Suspense wrapper ───
function S({ children }: { children: ReactNode }) {
    return <Suspense fallback={<SuspenseLoader />}>{children}</Suspense>;
}

// ─── Lazy imports : Pages publiques ───
const SurveyResponses = lazyWithRetry(() => import("./pages/surveys-responses/SurveyResponses"));
const HelpCenterPage = lazyWithRetry(() => import("./pages/help/HelpCenterPage"));
const HelpArticlePage = lazyWithRetry(() => import("./pages/help/HelpArticlePage"));
const HelpCategoryPage = lazyWithRetry(() => import("./pages/help/HelpCategoryPage"));
const OptOut = lazyWithRetry(() => import("./pages/customer/OptOut"));
const OptOutPhone = lazyWithRetry(() => import("./pages/customer/OptOutPhone"));
const PublicReservationLocks = lazyWithRetry(() => import("./pages/public-locks/PublicReservationLocks"));
const PublicReservationCalendar = lazyWithRetry(() => import("./pages/public-calendar/PublicReservationCalendar"));
const PublicCustomCalendar = lazyWithRetry(() => import("./pages/public-calendar/PublicCustomCalendar"));
const PublicReservationWallet = lazyWithRetry(() => import("./pages/public-wallet/PublicReservationWallet"));
const ReservationScanPage = lazyWithRetry(() => import("./pages/public-scan/ReservationScanPage"));
const PublicSalesView = lazyWithRetry(() => import("./pages/public-sales-view/PublicSalesView"));
const PublicOccupantsView = lazyWithRetry(() => import("./pages/public-occupants-view/PublicOccupantsView"));
const PublicSalon = lazyWithRetry(() => import("./pages/public-salon/PublicSalon"));
const PublicSalonFlyer = lazyWithRetry(() => import("./pages/public-salon-flyer/PublicSalonFlyer"));
const PublicSalonTeam = lazyWithRetry(() => import("./pages/public-salon-team/PublicSalonTeam"));
const PublicSalonDiagnostic = lazyWithRetry(() => import("./pages/public-salon-diagnostic/PublicSalonDiagnostic"));
const PublicSalonPartner = lazyWithRetry(() => import("./pages/public-salon-partner/PublicSalonPartner"));
const PublicWebinar = lazyWithRetry(() => import("./pages/public-webinar/PublicWebinar"));
const PublicCheckinKiosk = lazyWithRetry(() => import("./pages/public-checkin/PublicCheckinKiosk"));
const StandaloneChatPage = lazyWithRetry(() => import("./pages/standalone-chat/StandaloneChatPage"));

// ─── Lazy imports : Auth ───
const Login = lazyWithRetry(() => import("./pages/auth/Login"));
const Signup = lazyWithRetry(() => import("./pages/auth/Signup"));
const ForgotPassword = lazyWithRetry(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazyWithRetry(() => import("./pages/auth/ResetPassword"));
const SsoCallback = lazyWithRetry(() => import("./pages/auth/SsoCallback"));
const SsoError = lazyWithRetry(() => import("./pages/auth/SsoError"));

// ─── Lazy imports : App authentifiée ───
const Dashboard = lazyWithRetry(() => import("./pages/dashboard/Dashboard"));
const MessagingIndex = lazyWithRetry(() => import("./pages/messaging/Index"));
const CheckinPage = lazyWithRetry(() => import("./pages/checkin/CheckinPage"));
const EmptyScope = lazyWithRetry(() => import("./pages/errors/EmptyScope"));

// Flows
const FlowsIndex = lazyWithRetry(() => import("./pages/flows/Index"));
const FlowsUpdate = lazyWithRetry(() => import("./pages/flows/Update"));
const FlowsCreate = lazyWithRetry(() => import("./pages/flows/Create"));
const FlowStatistics = lazyWithRetry(() => import("./pages/flows/statistics/Statistics"));
const FlowAnalyse = lazyWithRetry(() => import("./pages/flows/statistics/Analyse"));
const FlowFlow = lazyWithRetry(() => import("./pages/flows/statistics/Flow"));
const FlowLogs = lazyWithRetry(() => import("./pages/flows/statistics/Logs"));
const FlowHeatmap = lazyWithRetry(() => import("./pages/flows/statistics/Heatmap"));

// Segments
const SegmentsIndex = lazyWithRetry(() => import("./pages/segments/Index"));
const SegmentsUpdate = lazyWithRetry(() => import("./pages/segments/Update"));

// Surveys (admin)
const SurveysIndex = lazyWithRetry(() => import("./pages/surveys/Index"));
const SurveysCreate = lazyWithRetry(() => import("./pages/surveys/Create"));
const SurveysUpdate = lazyWithRetry(() => import("./pages/surveys/Update"));
const SurveysResponses = lazyWithRetry(() => import("./pages/surveys/Responses"));
const SurveyGroupVariablesIndex = lazyWithRetry(() => import("./pages/surveys/group-variables/Index"));
const SurveyAnalyticsResults = lazyWithRetry(() => import("./pages/surveys/analytics/Results"));
const SurveyAnalyticsResponse = lazyWithRetry(() => import("./pages/surveys/analytics/Response"));
const SurveyAnalyticsSummary = lazyWithRetry(() => import("./pages/surveys/analytics/Summary"));

// Cardex
const CardexIndex = lazyWithRetry(() => import("./pages/cardex/Index"));
const CompaniesIndex = lazyWithRetry(() => import("./pages/companies/Index"));
const CompaniesDetected = lazyWithRetry(() => import("./pages/companies/Detected"));
const CompanyPage = lazyWithRetry(() => import("./pages/companies/CompanyPage"));
const CompaniesGroupIndex = lazyWithRetry(() => import("./pages/companies/CompaniesGroupIndex"));
const CompanyGroupPage = lazyWithRetry(() => import("./pages/companies/CompanyGroupPage"));
const HotelEventsIndex = lazyWithRetry(() => import("./pages/hotel-events/HotelEventsIndex"));
const HotelEventPage = lazyWithRetry(() => import("./pages/hotel-events/HotelEventPage"));
const CardexTabsPage = lazyWithRetry(() => import("./pages/cardex/customer/CardexTabsPage"));
const NeedsReview = lazyWithRetry(() => import("./pages/cardex/NeedsReview"));
const MergeSuggestions = lazyWithRetry(() => import("./pages/cardex/MergeSuggestions"));

// Reservations
const ReservationsIndex = lazyWithRetry(() => import("./pages/reservations/Index"));
const ReservationPrintPage = lazyWithRetry(() => import("./pages/reservations/print/ReservationPrintPage"));

// Email Templates
const EmailTemplatesIndex = lazyWithRetry(() => import("./pages/email-templates/Index"));
// Aiguillage MJML / HTML : un template HTML ne doit jamais ouvrir GrapesJS.
const EmailTemplatesEdit = lazyWithRetry(() => import("./pages/email-templates/Edit/EditEntry"));
const EmailTemplatesCreate = lazyWithRetry(() => import("./pages/email-templates/Create"));
const EmailGroupVariablesIndex = lazyWithRetry(() => import("./pages/email-templates/group-variables/Index"));

// Products
const ProductsEdition = lazyWithRetry(() => import("./pages/products/products/Products"));
const ProductsUpdate = lazyWithRetry(() => import("./pages/products/products/Update"));
const ServicesEdition = lazyWithRetry(() => import("./pages/products/services/Services"));
const ServicesUpdate = lazyWithRetry(() => import("./pages/products/services/Update"));
const RoomsEdition = lazyWithRetry(() => import("./pages/products/rooms/Rooms"));
const RatePlansEdition = lazyWithRetry(() => import("./pages/products/rate-plans/RatePlans"));
const CatalogsEdition = lazyWithRetry(() => import("./pages/products/catalogs/Catalogs"));
const CatalogsUpdate = lazyWithRetry(() => import("./pages/products/catalogs/Update"));
const SalesPagesEdition = lazyWithRetry(() => import("./pages/products/sales-pages/SalePages"));
const OrdersIndex = lazyWithRetry(() => import("./pages/products/orders/Orders"));
const OrdersUpdate = lazyWithRetry(() => import("./pages/products/orders/Update"));
const ProductSalesStats = lazyWithRetry(() => import("./pages/products/statistics/ProductSalesStats"));
const ProductCategories = lazyWithRetry(() => import("./pages/products/products/categories/Categories"));
const ProductCategoryUpdate = lazyWithRetry(() => import("./pages/products/products/categories/Update"));
const ServiceCategories = lazyWithRetry(() => import("./pages/products/services/categories/Categories"));
const ServiceCategoryUpdate = lazyWithRetry(() => import("./pages/products/services/categories/Update"));
const UpgradeManagement = lazyWithRetry(() => import("./pages/settings/upgrade-rules/UpgradeManagement"));
const BookingRequests = lazyWithRetry(() => import("./pages/booking-requests/requests/BookingRequests"));
const PublicBookingRequest = lazyWithRetry(() => import("./pages/public-booking-request/PublicBookingRequest"));
const BookablePlaces = lazyWithRetry(() => import("./pages/booking-requests/places/BookablePlaces"));
const BookablePlaceEdit = lazyWithRetry(() => import("./pages/booking-requests/places/BookablePlaceEdit"));
const OrderSettings = lazyWithRetry(() => import("./pages/products/order-settings/OrderSettings"));
// Payments
const PaymentsDashboard = lazyWithRetry(() => import("./pages/payments/Dashboard"));
const PaymentLinksIndex = lazyWithRetry(() => import("./pages/payments/links/Index"));
const PaymentLinksEdit = lazyWithRetry(() => import("./pages/payments/links/Edit"));
const PaymentConfig = lazyWithRetry(() => import("./pages/payments/config/PaymentConfig"));
const PaymentTransactionsIndex = lazyWithRetry(() => import("./pages/payments/transactions/Index"));
const PaymentCardsIndex = lazyWithRetry(() => import("./pages/payments/cards/Index"));
const PublicPaymentPage = lazyWithRetry(() => import("./pages/payments/public/PublicPaymentPage"));

// Loyalty
const LoyaltyRedirect = lazyWithRetry(() => import("./pages/loyalty/LoyaltyRedirect"));
const LoyaltyMembers = lazyWithRetry(() => import("./pages/loyalty/members/Members"));
const LoyaltyRewards = lazyWithRetry(() => import("./pages/loyalty/rewards/Rewards"));
const LoyaltySettings = lazyWithRetry(() => import("./pages/loyalty/settings/Settings"));
const LoyaltyFaq = lazyWithRetry(() => import("./pages/loyalty/faq/Faq"));

// Housekeeping
const HousekeepingLayout = lazyWithRetry(() => import("./pages/housekeeping/HousekeepingLayout"));
const HousekeepingBoard = lazyWithRetry(() => import("./pages/housekeeping/HousekeepingBoard"));
const HousekeepingMyRooms = lazyWithRetry(() => import("./pages/housekeeping/my-rooms/MyRooms"));
const HousekeepingAssignment = lazyWithRetry(() => import("./pages/housekeeping/assignment/AssignmentPage"));
const HousekeepingStats = lazyWithRetry(() => import("./pages/housekeeping/stats/Stats"));
const HousekeepingSettings = lazyWithRetry(() => import("./pages/housekeeping/settings/Settings"));
const HousekeepingLostFound = lazyWithRetry(() => import("./pages/housekeeping/lost-found/LostFound"));
const HousekeepingMaintenance = lazyWithRetry(() => import("./pages/housekeeping/maintenance/Maintenance"));
const HousekeepingCleaningPage = lazyWithRetry(() => import("./pages/housekeeping/my-rooms/CleaningPage"));
const HousekeepingMyReports = lazyWithRetry(() => import("./pages/housekeeping/my-rooms/MyReports"));
const HousekeepingMessages = lazyWithRetry(() => import("./pages/housekeeping/messages/MessagesPage"));

// Modules
const ModulesIndex = lazyWithRetry(() => import("./pages/modules/Index"));
const ModuleDetail = lazyWithRetry(() => import("./pages/modules/ModuleDetail"));

// Reviews
const Reviews = lazyWithRetry(() => import("./pages/reviews/Reviews"));
const ReviewAnalytics = lazyWithRetry(() => import("./pages/reviews/ReviewAnalytics"));

// Newsletter Widgets
const NewsletterWidgetsIndex = lazyWithRetry(() => import("./pages/newsletter-widgets/Index"));
const NewsletterWidgetsUpdate = lazyWithRetry(() => import("./pages/newsletter-widgets/Update"));

// Review Widgets
const ReviewWidgetsIndex = lazyWithRetry(() => import("./pages/review-widgets/Index"));
const ReviewWidgetsUpdate = lazyWithRetry(() => import("./pages/review-widgets/Update"));

// Files
const FilesIndex = lazyWithRetry(() => import("./pages/files/Index"));

// BI
const BiLayout = lazyWithRetry(() => import("./pages/bi/BiLayout"));
const BiOverview = lazyWithRetry(() => import("./pages/bi/pages/Overview"));
const BiRevenue = lazyWithRetry(() => import("./pages/bi/pages/Revenue"));
const BiClients = lazyWithRetry(() => import("./pages/bi/pages/Clients"));
const BiCommunication = lazyWithRetry(() => import("./pages/bi/pages/Communication"));
const BiReputation = lazyWithRetry(() => import("./pages/bi/pages/Reputation"));
const BiDistribution = lazyWithRetry(() => import("./pages/bi/pages/Distribution"));
const BiChannelMappings = lazyWithRetry(() => import("./pages/bi/pages/ChannelMappings"));

// Settings
const SettingsRedirect = lazyWithRetry(() => import("./pages/settings/components/SettingsRedirect"));
const Facilities = lazyWithRetry(() => import("./pages/settings/facilities/Facilities"));
const IdentityProfileTab = lazyWithRetry(() => import("./pages/settings/facilities/IdentityProfileTab"));
const AccountSecurityPage = lazyWithRetry(() => import("./pages/settings/account-security/AccountSecurityPage"));
const CommunicationsSettings = lazyWithRetry(() => import("./pages/settings/communications/CommunicationsSettings"));
const AiSettingsRedirect = lazyWithRetry(() => import("./pages/settings/ai/AiSettingsRedirect"));
const SiteChatSettingsPage = lazyWithRetry(() => import("./pages/settings/site-chat/SiteChatSettingsPage"));
const McpAuthorizePage = lazyWithRetry(() => import("./pages/oauth/AuthorizePage"));
const MetaSettings = lazyWithRetry(() => import("./pages/settings/meta/MetaSettings"));
const SelectMetaPage = lazyWithRetry(() => import("./pages/settings/meta/SelectPage"));
const BrandingsIndex = lazyWithRetry(() => import("./pages/settings/brandings/Index"));
const BrandingsUpdate = lazyWithRetry(() => import("./pages/settings/brandings/Update/Update"));
const PublicPagesIndex = lazyWithRetry(() => import("./pages/settings/public-pages/PublicPagesIndex"));
const PublicLockContactBlockPage = lazyWithRetry(() => import("./pages/settings/public-pages/PublicLockContactBlockPage"));
const ConnectivitiesIndex = lazyWithRetry(() => import("./pages/settings/connectivities/Index"));
const ConnectivitiesEdit = lazyWithRetry(() => import("./pages/settings/connectivities/Edit"));
const FacilityGroupsIndex = lazyWithRetry(() => import("./pages/settings/facility-groups/Index"));
const FacilityGroupsMappingValue = lazyWithRetry(() => import("./pages/settings/facility-groups/MappingValue"));
const UsersIndex = lazyWithRetry(() => import("./pages/settings/users/Index"));
const UsersEdit = lazyWithRetry(() => import("./pages/settings/users/Edit"));
const RedirectLinksIndex = lazyWithRetry(() => import("./pages/settings/redirect-links/Index"));
const MappingKeysIndex = lazyWithRetry(() => import("./pages/settings/mapping-keys/Index"));
const TagsIndex = lazyWithRetry(() => import("./pages/settings/tags/Index"));
const RolesIndex = lazyWithRetry(() => import("./pages/settings/roles/Index"));
const GroupChildrenIndex = lazyWithRetry(() => import("./pages/settings/group-children/Index"));
const GroupChildrenTagsIndex = lazyWithRetry(() => import("./pages/settings/group-children/tags/Index"));
import { HotelGroupsIndex } from "./pages/settings/hotel-groups/hotelGroupsRoutes";

// Onboarding
import RequireOnboardingAccess from "./pages/onboarding/RequireOnboardingAccess";
import { OnboardingProvider } from "./pages/onboarding/OnboardingContext";
import OnboardingLayout from "./pages/onboarding/OnboardingLayout";
const OnboardingPage = lazyWithRetry(() => import("./pages/onboarding/OnboardingPage"));
const QuickStartPage = lazyWithRetry(() => import("./pages/flows/QuickStartPage"));
import QuickStartLinkGate from "./pages/flows/QuickStartLinkGate";
import AiAssistantRedirect from "./pages/email-templates/AiAssistantRedirect";

// Super-admin
const SuperAdminDashboardPage = lazyWithRetry(() => import("./pages/super-admin/dashboard/SuperAdminDashboardPage"));
const SAFacilitiesIndex = lazyWithRetry(() => import("./pages/super-admin/facilities/Index"));
const SAFacilitiesUpdate = lazyWithRetry(() => import("./pages/super-admin/facilities/Update"));
const AnalyticsLayout = lazyWithRetry(() => import("./pages/super-admin/analytics/AnalyticsLayout"));
const AnalyticsGeneral = lazyWithRetry(() => import("./pages/super-admin/analytics/AnalyticsGeneral"));
const AiStatsPage = lazyWithRetry(() => import("./pages/super-admin/analytics/ai/AiStatsPage"));
const CrmLemcalToAttach = lazyWithRetry(() => import("./pages/super-admin/crm/toClassify/LemcalToAttachPage"));
const CrmIndex = lazyWithRetry(() => import("./pages/super-admin/crm/Index"));
const CrmTasks = lazyWithRetry(() => import("./pages/super-admin/crm/Tasks"));
const CrmEmailTemplates = lazyWithRetry(() => import("./pages/super-admin/crm/EmailTemplates"));
const CrmNootaMeetings = lazyWithRetry(() => import("./pages/super-admin/crm/NootaMeetings"));
const CrmCalendarEvents = lazyWithRetry(() => import("./pages/super-admin/crm/CrmCalendarEvents"));
const CrmDocumentViews = lazyWithRetry(() => import("./pages/super-admin/crm/DocumentViews"));
const CrmEmails = lazyWithRetry(() => import("./pages/super-admin/crm/CrmEmails"));
const CrmFacilityDetail = lazyWithRetry(() => import("./pages/super-admin/crm/FacilityDetail"));
const CrmProspectDetail = lazyWithRetry(() => import("./pages/super-admin/crm/ProspectDetail"));
const CrmSalonEvents = lazyWithRetry(() => import("./pages/super-admin/crm/salon/SalonEvents"));
const CrmEventDetail = lazyWithRetry(() => import("./pages/super-admin/crm/salon/EventDetail"));
const CrmInterestSettings = lazyWithRetry(() => import("./pages/super-admin/crm/salon/InterestSettings"));
const CrmDemandes = lazyWithRetry(() => import("./pages/super-admin/crm/demandes/CrmDemandes"));
const CrmProspectingCampaigns = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/CampaignsPage"));
const CrmProspectingCampaign = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/CampaignDetailPage"));
const CrmProspectingTemplate = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/TemplateEditorPage"));
const CrmProspectingWorkshop = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/atelier/WorkshopPage"));
const CrmProspectingWorkshopNew = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/atelier/NewWorkshopPage"));
const CrmProspectingReview = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/ReviewPage"));
const CrmProspectingReplies = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/reponses/RepliesPage"));
const CrmProspectingImport = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/ImportPage"));
const CrmProspectingMailboxes = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/MailboxesPage"));
const CrmProspectingRecapSettings = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/RecapSettingsPage"));
const CrmProspectingToday = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/today/TodayPage"));
const CrmProspectingSends = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/envois/SendsPage"));
const CrmProspectingHotels = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/hotels/HotelsPage"));
const CrmProspectingHotel = lazyWithRetry(() => import("./pages/super-admin/crm/prospection/hotels/HotelDetailPage"));
const SAConfigRoles = lazyWithRetry(() => import("./pages/super-admin/config/roles/Index"));
const SAConfigPlugs = lazyWithRetry(() => import("./pages/super-admin/plugs/Plugs"));
const SAConfigAi = lazyWithRetry(() => import("./pages/super-admin/ai/AiManagement"));
const SAConfigPmsConnectivity = lazyWithRetry(() => import("./pages/super-admin/config/pms-connectivity/Index"));
const SAConfigSaml = lazyWithRetry(() => import("./pages/super-admin/config/saml/Index"));
const SAConfigMjmlRules = lazyWithRetry(() => import("./pages/super-admin/config/mjml-rules/Index"));
const SAConfigAiEmailTypes = lazyWithRetry(() => import("./pages/super-admin/config/email-types/Index"));
const SAConfigPmsDetail = lazyWithRetry(() => import("./pages/super-admin/config/pms-connectivity/PmsDetail"));
const SAChangelogIndex = lazyWithRetry(() => import("./pages/super-admin/changelog/Index"));
const SAChangelogForm = lazyWithRetry(() => import("./pages/super-admin/changelog/Form"));
const SALandingBacklinksIndex = lazyWithRetry(() => import("./pages/super-admin/seo/landing-backlinks/Index"));
const SAFlowTemplatesIndex = lazyWithRetry(() => import("./pages/super-admin/flow-templates/Index"));
const SAFlowTemplateTypesIndex = lazyWithRetry(() => import("./pages/super-admin/flow-template-types/Index"));
const SAEmailStyleMatrixIndex = lazyWithRetry(() => import("./pages/super-admin/email-styles/Index"));
const SAFlowTemplateDrafts = lazyWithRetry(() => import("./pages/super-admin/flow-templates/Drafts"));
const SAEmailLibrary = lazyWithRetry(() => import("./pages/super-admin/email-library/Index"));
const SASurveyLibrary = lazyWithRetry(() => import("./pages/super-admin/survey-library/Index"));
const SAAiReferenceEmailsIndex = lazyWithRetry(() => import("./pages/super-admin/ai-reference-emails/Index"));
const FlowStatsPage = lazyWithRetry(() => import("./pages/super-admin/flow-stats/FlowStatsPage"));
const FlowTaggingPage = lazyWithRetry(() => import("./pages/super-admin/flow-stats/FlowTaggingPage"));
const FlowHealthPage = lazyWithRetry(() => import("./pages/super-admin/flow-stats/FlowHealthPage"));
const HkMessagingStatsPage = lazyWithRetry(() => import("./pages/super-admin/housekeeping-messaging/HkMessagingStatsPage"));
const LeadMagnetsPage = lazyWithRetry(() => import("./pages/super-admin/lead-magnets/LeadMagnetsPage"));
const LeadMagnetDetailPage = lazyWithRetry(() => import("./pages/super-admin/lead-magnets/LeadMagnetDetailPage"));
const SupportInboxPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportInboxPage"));
const SupportUnansweredPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportUnansweredPage"));
const SupportStatsPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportStatsPage"));
const SupportSettingsPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportSettingsPage"));
const SupportModelsPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportModelsPage"));
const AiModelsPage = lazyWithRetry(() => import("./pages/super-admin/ai/llm/AiModelsPage"));
const AiQualityPage = lazyWithRetry(() => import("./pages/super-admin/ai/llm/AiQualityPage"));
const SupportManualPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportManualPage"));
const SupportManualProposalPage = lazyWithRetry(() => import("./pages/super-admin/support/SupportManualProposalPage"));
const HelpArticlesPage = lazyWithRetry(() => import("./pages/super-admin/support/HelpArticlesPage"));
const HelpCategoriesPage = lazyWithRetry(() => import("./pages/super-admin/support/HelpCategoriesPage"));
const HelpArticleEditPage = lazyWithRetry(() => import("./pages/super-admin/support/HelpArticleEditPage"));
const BlogReviewsPage = lazyWithRetry(() => import("./pages/super-admin/blog/BlogReviewsPage"));
const BlogReviewDetailPage = lazyWithRetry(() => import("./pages/super-admin/blog/BlogReviewDetailPage"));
const SADemoReseedPage = lazyWithRetry(() => import("./pages/super-admin/demo/DemoReseedPage"));

// Customer Space
const CustomerDashboard = lazyWithRetry(() => import("./pages/customer-space/dashboard/CustomerDashboard"));
const CSFaq = lazyWithRetry(() => import("./pages/customer-space/faq/CSFaq"));
const CSContact = lazyWithRetry(() => import("./pages/customer-space/contact/CSContact"));
const CSBenefitsPage = lazyWithRetry(() => import("./pages/customer-space/benefits/CSBenefitsPage"));
const CSBenefitsLevelPage = lazyWithRetry(() => import("./pages/customer-space/benefits/CSBenefitsLevelPage"));
const CSReservations = lazyWithRetry(() => import("./pages/customer-space/customer-reservations/CSReservations"));
const CSLastReservations = lazyWithRetry(() => import("./pages/customer-space/customer-reservations/CSLastReservations"));
const CSReservation = lazyWithRetry(() => import("./pages/customer-space/customer-reservations/CSReservation"));
const CSReservationContact = lazyWithRetry(() => import("./pages/customer-space/customer-reservations/CSReservationContact"));
const CSReservationLocks = lazyWithRetry(() => import("./pages/customer-space/customer-reservations/CSReservationLocks"));
const CSCatalogs = lazyWithRetry(() => import("./pages/customer-space/catalogs/CSCatalogs"));
const CSCatalog = lazyWithRetry(() => import("./pages/customer-space/catalogs/CSCatalog"));
const CSLoyaltyCartPage = lazyWithRetry(() => import("./pages/customer-space/catalogs/CSLoyaltyCartPage"));
const CSOrders = lazyWithRetry(() => import("./pages/customer-space/orders/CSOrders"));
const CSOrder = lazyWithRetry(() => import("./pages/customer-space/orders/CSOrder"));
const CSSettings = lazyWithRetry(() => import("./pages/customer-space/settings/CSSettings"));
const CSSettingsInformations = lazyWithRetry(() => import("./pages/customer-space/settings/CSSettingsInformations"));
const CSHotelsPage = lazyWithRetry(() => import("./pages/customer-space/hotels/CSHotelsPage"));

// ═══════════════════════════════════════════════════════
// Router
// ═══════════════════════════════════════════════════════
const router = createBrowserRouter([
    // Partner messaging
    {
        path: "partner/messaging/:partner/:key",
        element: <S><MessagingIndex /></S>,
    },

    // Customer Space
    {
        path: "/customer-space",
        element: <RequireCustomer/>,
        children: [
            {
                element: <CustomerSpaceLayout/>,
                children: [
                    { index: true, element: <Navigate to="dashboard" />, handle: { title: "Compte" } },
                    { path: '', element: <S><CustomerDashboard /></S> },
                    { path: "dashboard", element: <S><CustomerDashboard /></S>, handle: { title: "Compte" } },
                    { path: "dashboard/faq", element: <S><CSFaq /></S>, handle: { title: "FAQ" } },
                    { path: "dashboard/contact", element: <S><CSContact /></S>, handle: { title: "Contact" } },
                    {
                        path: "reservations",
                        handle: { title: "Réservations" },
                        children: [
                            { path: '', element: <Navigate to='/customer-space/reservations/upcoming'/> },
                            { path: 'upcoming', element: <S><CSReservations /></S> },
                            { path: 'last', element: <S><CSLastReservations /></S> },
                            { path: 'details/:reservationId', element: <S><CSReservation /></S> },
                            { path: 'details/:reservationId/contact', element: <S><CSReservationContact /></S> },
                            { path: 'details/:reservationId/locks', element: <S><CSReservationLocks /></S> },
                        ],
                    },
                    {
                        path: "avantages",
                        handle: { title: "Catalogues" },
                        children: [
                            { path: '', element: <Navigate to='/customer-space/avantages/index'/> },
                            { path: 'index', element: <S><CSCatalogs /></S> },
                            { path: 'catalog/:idCatalog', element: <S><CSCatalog /></S> },
                            { path: 'cart', element: <S><CSLoyaltyCartPage /></S> },
                        ],
                    },
                    { path: "benefits", element: <S><CSBenefitsPage /></S>, handle: { title: "Avantages" } },
                    { path: "benefits/:levelId", element: <S><CSBenefitsLevelPage /></S>, handle: { title: "Avantages" } },
                    {
                        path: "orders",
                        handle: { title: "Commandes" },
                        children: [
                            { path: '', element: <Navigate to='/customer-space/orders/index'/> },
                            { path: 'index', element: <S><CSOrders /></S> },
                            { path: 'show/:idOrder', element: <S><CSOrder /></S> },
                        ],
                    },
                    { path: "hotels", element: <S><CSHotelsPage /></S>, handle: { title: "Nos hôtels" } },
                    {
                        path: "settings",
                        handle: { title: "Paramètres" },
                        children: [
                            { path: '', element: <Navigate to='/customer-space/settings/global'/> },
                            { path: 'global', element: <S><CSSettings /></S> },
                            { path: 'informations', element: <S><CSSettingsInformations /></S> },
                        ],
                    },
                ],
            },
        ],
    },

    // Routes authentifiées (staff)
    {
        path: '/',
        element: <RequireStaff/>,
        loader: authLoader,
        children: [
            {
                path: 'checkin',
                children: [
                    { index: true, element: <S><CheckinPage /></S> },
                ],
            },
            {
                // Périmètre du compte vide (403 empty_scope) : écran dédié, hors DefaultLayout.
                path: 'empty-scope',
                element: <S><EmptyScope /></S>,
            },
            {
                // Écran de consentement OAuth des clients MCP (claude.ai, ChatGPT…).
                // Authentifié (redirection /login avec retour) mais hors DefaultLayout.
                path: 'oauth/authorize',
                element: <S><McpAuthorizePage /></S>,
            },
            {
                path: 'onboarding/:step?',
                element: (
                    <RequireOnboardingAccess>
                        <OnboardingProvider>
                            <OnboardingLayout>
                                <S><OnboardingPage /></S>
                            </OnboardingLayout>
                        </OnboardingProvider>
                    </RequireOnboardingAccess>
                ),
            },
            {
                // Démarrage rapide en plein écran, hors DefaultLayout : le même écran
                // que l'étape « Vos premiers parcours » de l'assistant.
                element: <RequirePermission/>,
                children: [
                    { path: 'flows/quick-start', element: <S><QuickStartPage /></S> },
                    // Feuille imprimable de la réception : sans menu ni en-tête de l'app.
                    { path: 'reservations/print', element: <S><ReservationPrintPage /></S> },
                ],
            },
            {
                element: <DefaultLayout/>,
                children: [
                    {
                        element: <RequirePermission/>,
                        children: [
                            { path: '/', element: <SmartRedirect/> },
                            { path: 'dashboard', element: <S><Dashboard /></S> },

                            // Centre d'aide : ouvert à tout compte connecté (l'API ne sert que les articles publics publiés)
                            { path: 'aide', element: <S><HelpCenterPage /></S> },
                            { path: 'aide/c/:categorySlug', element: <S><HelpCategoryPage /></S> },
                            { path: 'aide/:slug', element: <S><HelpArticlePage /></S> },

                            // Segments
                            {
                                path: 'segments',
                                children: [
                                    { path: '', element: <S><SegmentsIndex /></S> },
                                    { path: 'update/:idSegment', element: <S><SegmentsUpdate /></S> },
                                ],
                            },

                            // Flows
                            {
                                path: 'flows',
                                children: [
                                    { path: '', element: <S><QuickStartLinkGate><FlowsIndex /></QuickStartLinkGate></S> },
                                    { path: 'update/:idFlowStarter', element: <S><FlowsUpdate /></S> },
                                    {
                                        path: 'statistics/:idFlowStarter',
                                        children: [
                                            { path: 'overview', element: <S><FlowStatistics /></S> },
                                            { path: 'analyse', element: <S><FlowAnalyse /></S> },
                                            { path: 'flow', element: <S><FlowFlow /></S> },
                                            { path: 'logs', element: <S><FlowLogs /></S> },
                                            { path: 'heatmap', element: <S><FlowHeatmap /></S> },
                                        ],
                                    },
                                    { path: 'create', element: <S><FlowsCreate /></S> },
                                    // Ancienne adresse du démarrage rapide.
                                    { path: 'onboarding', element: <Navigate to="/flows/quick-start" replace /> },
                                ],
                            },

                            // Surveys (admin)
                            {
                                path: 'surveys',
                                children: [
                                    { path: '', element: <Navigate to='/surveys/responses'/> },
                                    { path: 'list', element: <S><SurveysIndex /></S> },
                                    { path: 'templates', element: <S><SurveysIndex /></S> },
                                    { path: 'responses', element: <S><SurveysResponses /></S> },
                                    { path: 'list/update/:idSurvey', element: <S><SurveysUpdate /></S> },
                                    {
                                        path: 'list/responses/:idSurvey',
                                        children: [
                                            { path: '', element: <S><SurveyAnalyticsResults /></S> },
                                            { path: ':surveyResponseId', element: <S><SurveyAnalyticsResponse /></S> },
                                            { path: 'summary', element: <S><SurveyAnalyticsSummary /></S> },
                                        ],
                                    },
                                    { path: 'list/create', element: <S><SurveysCreate /></S> },
                                    { path: 'group-variables', element: <S><SurveyGroupVariablesIndex /></S> },
                                ],
                            },

                            // Messaging
                            {
                                path: 'messaging',
                                children: [
                                    { path: '', element: <S><MessagingIndex /></S> },
                                    { path: ':customerId/:surveyResponseId?', element: <S><MessagingIndex /></S> },
                                ],
                            },

                            // Cardex
                            {
                                path: 'cardex',
                                children: [
                                    { path: '', element: <S><CardexIndex /></S> },
                                    { path: 'needs-review', element: <S><NeedsReview /></S> },
                                    { path: 'merge-suggestions', element: <S><MergeSuggestions /></S> },
                                    { path: ':idCustomer', element: <S><CardexTabsPage /></S> },
                                ],
                            },

                            // Sociétés
                            {
                                path: 'companies',
                                element: <CompaniesModuleGuard />,
                                children: [
                                    { path: '', element: <S><GroupContextGate group={<CompaniesGroupIndex />} hotel={<CompaniesIndex />} /></S> },
                                    { path: 'detected', element: <S><GroupContextGate hotel={<CompaniesDetected />} /></S> },
                                    { path: 'group/:key', element: <S><GroupContextGate group={<CompanyGroupPage />} /></S> },
                                    { path: ':idCompany', element: <S><GroupContextGate hotel={<CompanyPage />} /></S> },
                                ],
                            },

                            // Événements
                            {
                                path: 'events',
                                element: <HotelEventsModuleGuard />,
                                children: [
                                    { path: '', element: <S><HotelEventsIndex /></S> },
                                    { path: ':idEvent', element: <S><HotelEventPage /></S> },
                                ],
                            },

                            // Reservations
                            {
                                path: 'reservations',
                                children: [
                                    { path: '', element: <S><ReservationsIndex /></S> },
                                ],
                            },

                            // Products
                            {
                                path: 'products',
                                children: [
                                    { path: '', element: <Navigate to='/products/orders'/> },
                                    {
                                        path: 'list',
                                        children: [
                                            { path: '', element: <S><ProductsEdition /></S> },
                                            { path: 'update/:idProduct', element: <S><ProductsUpdate /></S> },
                                            {
                                                path: 'categories',
                                                children: [
                                                    { path: '', element: <S><ProductCategories /></S> },
                                                    { path: 'update/:idCategory', element: <S><ProductCategoryUpdate /></S> },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: 'services',
                                        children: [
                                            { path: '', element: <Navigate to='list'/> },
                                            { path: 'list', element: <S><ServicesEdition /></S> },
                                            { path: 'update/:idService', element: <S><ServicesUpdate /></S> },
                                            {
                                                path: 'categories',
                                                children: [
                                                    { path: '', element: <S><ServiceCategories /></S> },
                                                    { path: 'update/:idCategory', element: <S><ServiceCategoryUpdate /></S> },
                                                ],
                                            },
                                        ],
                                    },
                                    { path: 'rate-plans', element: <S><RatePlansEdition /></S> },
                                    {
                                        path: 'rooms',
                                        children: [
                                            { path: '', element: <Navigate to='list'/> },
                                            { path: 'list', element: <S><RoomsEdition /></S> },
                                            { path: 'update/:idService', element: <S><ServicesUpdate /></S> },
                                            { path: 'upgrade-management', element: <S><UpgradeManagement /></S> },
                                            {
                                                path: 'categories',
                                                children: [
                                                    { path: '', element: <S><ServiceCategories parentRoute="/products/rooms" parentLabel="Chambres" title="Catégories de chambre" subtitle="Organisez vos chambres par catégories" iconColor="teal" showUpgradeLevels /></S> },
                                                    { path: 'update/:idCategory', element: <S><ServiceCategoryUpdate /></S> },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: 'catalogs',
                                        children: [
                                            { path: '', element: <S><CatalogsEdition /></S> },
                                            { path: 'update/:idCatalog', element: <S><CatalogsUpdate /></S> },
                                        ],
                                    },
                                    {
                                        path: 'sales-pages',
                                        children: [
                                            { path: '', element: <S><SalesPagesEdition /></S> },
                                        ],
                                    },
                                    {
                                        path: 'orders',
                                        children: [
                                            { path: '', element: <S><OrdersIndex /></S> },
                                            { path: 'update/:idOrder', element: <S><OrdersUpdate /></S> },
                                        ],
                                    },
                                    {
                                        path: 'statistics',
                                        children: [
                                            { path: '', element: <S><ProductSalesStats /></S> },
                                        ],
                                    },
                                    {
                                        path: 'booking-requests',
                                        children: [
                                            { path: '', element: <S><BookingRequests /></S> },
                                        ],
                                    },
                                    {
                                        path: 'bookable-places',
                                        children: [
                                            { path: '', element: <S><BookablePlaces /></S> },
                                            { path: 'new', element: <S><BookablePlaceEdit /></S> },
                                            { path: ':idPlace', element: <S><BookablePlaceEdit /></S> },
                                        ],
                                    },
                                    {
                                        path: 'order-settings',
                                        children: [
                                            { path: '', element: <S><OrderSettings /></S> },
                                        ],
                                    },
                                ],
                            },

                            // Payments
                            {
                                path: 'payments',
                                children: [
                                    { path: '', element: <S><PaymentsDashboard /></S> },
                                    { path: 'transactions', element: <S><PaymentTransactionsIndex /></S> },
                                    { path: 'cards', element: <S><PaymentCardsIndex /></S> },
                                    {
                                        path: 'links',
                                        children: [
                                            { path: '', element: <S><PaymentLinksIndex /></S> },
                                            { path: ':id/edit', element: <S><PaymentLinksEdit /></S> },
                                        ],
                                    },
                                    { path: 'config', element: <S><PaymentConfig /></S> },
                                ],
                            },

                            // Email Templates
                            {
                                path: 'email-templates',
                                children: [
                                    { path: '', element: <S><EmailTemplatesIndex /></S> },
                                    { path: ':idEmailTemplate', element: <S><EmailTemplatesEdit /></S> },
                                    { path: 'create', element: <S><EmailTemplatesCreate /></S> },
                                    // L'assistant IA est une modale de la page de création.
                                    { path: 'ai/new', element: <AiAssistantRedirect /> },
                                    { path: 'group-variables', element: <S><EmailGroupVariablesIndex /></S> },
                                ],
                            },

                            // Loyalty
                            {
                                path: 'loyalty',
                                children: [
                                    { path: '', element: <S><LoyaltyRedirect /></S> },
                                    { path: 'members/:idProgram', element: <S><LoyaltyMembers /></S> },
                                    { path: 'rewards/:idProgram', element: <S><LoyaltyRewards /></S> },
                                    { path: 'faq/:idProgram', element: <S><LoyaltyFaq /></S> },
                                    { path: 'settings/:idProgram', element: <S><LoyaltySettings /></S> },
                                ],
                            },

                            // Reviews
                            {
                                path: 'reviews',
                                children: [
                                    { index: true, element: <S><Reviews /></S> },
                                    { path: 'google', element: <S><Reviews /></S> },
                                    { path: 'analytics', element: <S><ReviewAnalytics /></S> },
                                ],
                            },

                            // Housekeeping
                            {
                                path: 'housekeeping',
                                element: <S><HousekeepingLayout /></S>,
                                children: [
                                    { index: true, element: <S><HousekeepingBoard /></S> },
                                    { path: 'my-rooms', element: <S><HousekeepingMyRooms /></S> },
                                    { path: 'my-reports', element: <S><HousekeepingMyReports /></S> },
                                    { path: 'clean/:serviceId', element: <S><HousekeepingCleaningPage /></S> },
                                    { path: 'lost-found', element: <S><HousekeepingLostFound /></S> },
                                    { path: 'maintenance', element: <S><HousekeepingMaintenance /></S> },
                                    { path: 'messages', element: <S><HousekeepingMessages /></S> },
                                    { path: 'assignment', element: <S><HousekeepingAssignment /></S> },
                                    { path: 'stats', element: <S><HousekeepingStats /></S> },
                                    { path: 'settings', element: <S><HousekeepingSettings /></S> },
                                ],
                            },

                            // Modules
                            {
                                path: 'modules',
                                children: [
                                    { path: '', element: <S><ModulesIndex /></S> },
                                    { path: ':slug', element: <S><ModuleDetail /></S> },
                                ],
                            },

                            // Newsletter Widgets
                            {
                                path: 'newsletter-widgets',
                                children: [
                                    { path: '', element: <S><NewsletterWidgetsIndex /></S> },
                                    { path: 'update/:id', element: <S><NewsletterWidgetsUpdate /></S> },
                                ],
                            },

                            // Review Widgets (widgets d'avis embarquables)
                            {
                                path: 'review-widgets',
                                children: [
                                    { path: '', element: <S><ReviewWidgetsIndex /></S> },
                                    { path: 'update/:id', element: <S><ReviewWidgetsUpdate /></S> },
                                ],
                            },

                            // Files
                            { path: 'files', children: [{ path: '', element: <S><FilesIndex /></S> }] },

                            // BI
                            {
                                path: 'bi',
                                children: [
                                    {
                                        element: <S><BiLayout /></S>,
                                        children: [
                                            { index: true, element: <S><BiOverview /></S> },
                                            { path: 'revenue', element: <S><BiRevenue /></S> },
                                            { path: 'clients', element: <S><BiClients /></S> },
                                            { path: 'communication', element: <S><BiCommunication /></S> },
                                            { path: 'reputation', element: <S><BiReputation /></S> },
                                            { path: 'distribution', element: <S><BiDistribution /></S> },
                                        ],
                                    },
                                    { path: 'channel-mappings', element: <S><BiChannelMappings /></S> },
                                ],
                            },

                            // Changelog — now a global panel, redirect old route
                            { path: 'changelog', element: <Navigate to="/dashboard" replace /> },

                            // Settings
                            {
                                path: 'settings',
                                children: [
                                    { path: '', element: <S><SettingsRedirect /></S> },
                                    { path: 'facilities', element: <S><Facilities /></S> },
                                    { path: 'facilities/:idFacility', element: <S><Facilities /></S> },
                                    { path: 'identity', element: <S><IdentityProfileTab /></S> },
                                    { path: 'account-security', element: <S><AccountSecurityPage /></S> },
                                    { path: 'communications', element: <S><CommunicationsSettings /></S> },
                                    { path: 'email-packs', element: <Navigate to='/settings/communications?tab=emails' replace /> },
                                    { path: 'wallet-recharges', element: <Navigate to='/settings/communications?tab=sms' replace /> },
                                    { path: 'sms-geo-permissions', element: <Navigate to='/settings/communications?tab=restrictions' replace /> },
                                    { path: 'whatsapp', element: <Navigate to='/settings/communications?tab=whatsapp' replace /> },
                                    { path: 'mailboxes', element: <Navigate to='/settings/communications?tab=mailboxes' replace /> },
                                    { path: 'integrations', element: <Navigate to='/reviews' replace /> },
                                    { path: 'ai', element: <S><AiSettingsRedirect /></S> },
                                    { path: 'site-chat', element: <S><SiteChatSettingsPage /></S> },
                                    { path: 'meta', element: <Navigate to='/settings/communications?tab=meta' replace /> },
                                    { path: 'meta/select-page', element: <S><SelectMetaPage /></S> },
                                    { path: 'channel-mappings', element: <Navigate to='/bi/channel-mappings' replace /> },
                                    { path: 'live-chat', element: <Navigate to='/settings/site-chat?tab=widget' replace /> },
                                    {
                                        path: 'brandings',
                                        children: [
                                            { path: '', element: <S><BrandingsIndex /></S> },
                                            { path: ':idBranding', element: <S><BrandingsUpdate /></S> },
                                        ],
                                    },
                                    {
                                        path: 'public-pages',
                                        children: [
                                            { path: '', element: <S><PublicPagesIndex /></S> },
                                            { path: 'lock', element: <S><PublicLockContactBlockPage /></S> },
                                        ],
                                    },
                                    {
                                        path: 'redirect',
                                        children: [
                                            { path: '', element: <S><RedirectLinksIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'tags',
                                        children: [
                                            { path: '', element: <S><TagsIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'group-children',
                                        children: [
                                            { path: '', element: <S><GroupChildrenIndex /></S> },
                                            { path: 'tags', element: <S><GroupChildrenTagsIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'hotel-groups',
                                        children: [
                                            { path: '', element: <S><HotelGroupsIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'mapping-keys',
                                        children: [
                                            { path: '', element: <S><MappingKeysIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'connectivities',
                                        children: [
                                            { path: '', element: <S><ConnectivitiesIndex /></S> },
                                            { path: ':idConnectivity', element: <S><ConnectivitiesEdit /></S> },
                                        ],
                                    },
                                    {
                                        path: 'facility-groups',
                                        children: [
                                            { path: '', element: <S><FacilityGroupsIndex /></S> },
                                            { path: ':facilityGroupId/mapping-values', element: <S><FacilityGroupsMappingValue /></S> },
                                        ],
                                    },
                                    {
                                        path: 'users',
                                        children: [
                                            { path: '', element: <S><UsersIndex /></S> },
                                            { path: ':idUser', element: <S><UsersEdit /></S> },
                                        ],
                                    },
                                    {
                                        path: 'roles',
                                        children: [
                                            { path: '', element: <S><RolesIndex /></S> },
                                        ],
                                    },
                                ],
                            },

                            // Super-admin
                            {
                                path: 'super-admin',
                                // En-têtes du back-office à la couleur de la charte par défaut.
                                element: <PageHeaderDefaultsProvider iconColor="brand"><Outlet /></PageHeaderDefaultsProvider>,
                                children: [
                                    // Accueil du mode super admin : tableau de bord staff.
                                    { path: '', element: <S><SuperAdminDashboardPage /></S> },
                                    {
                                        path: 'facilities',
                                        children: [
                                            { path: '', element: <S><SAFacilitiesIndex /></S> },
                                            { path: 'update/:idFacility', element: <S><SAFacilitiesUpdate /></S> },
                                        ],
                                    },
                                    { path: 'demo', element: <S><SADemoReseedPage /></S> },
                                    {
                                        path: 'crm',
                                        children: [
                                            { path: '', element: <S><CrmIndex /></S> },
                                            { path: 'tasks', element: <S><CrmTasks /></S> },
                                            { path: 'email-templates', element: <S><CrmEmailTemplates /></S> },
                                            { path: 'documents-partages', element: <S><CrmDocumentViews /></S> },
                                            { path: 'noota-meetings', element: <S><CrmNootaMeetings /></S> },
                                            { path: 'agenda', element: <S><CrmCalendarEvents /></S> },
                                            { path: 'emails', element: <S><CrmEmails /></S> },
                                            { path: 'rdv-a-rattacher', element: <S><CrmLemcalToAttach /></S> },
                                            { path: 'facilities/:facilityId', element: <S><CrmFacilityDetail /></S> },
                                            { path: 'prospects/:prospectId', element: <S><CrmProspectDetail /></S> },
                                            { path: 'salons', element: <S><CrmSalonEvents /></S> },
                                            { path: 'salons/interets', element: <S><CrmInterestSettings /></S> },
                                            { path: 'salons/:eventId/:tab?', element: <S><CrmEventDetail /></S> },
                                            { path: 'demandes', element: <S><CrmDemandes /></S> },
                                            { path: 'prospection', element: <S><CrmProspectingToday /></S> },
                                            { path: 'prospection/envois', element: <S><CrmProspectingSends /></S> },
                                            { path: 'prospection/hotels', element: <S><CrmProspectingHotels /></S> },
                                            { path: 'prospection/hotels/:hotelId', element: <S><CrmProspectingHotel /></S> },
                                            { path: 'prospection/reponses/:onglet?', element: <S><CrmProspectingReplies /></S> },
                                            { path: 'prospection/relecture', element: <S><CrmProspectingReview /></S> },
                                            { path: 'prospection/campagnes', element: <S><CrmProspectingCampaigns /></S> },
                                            // Sans onglet, ou ancien ?onglet=x : redirigé vers /campagnes/:id/x (Vue d'ensemble par défaut).
                                            { path: 'prospection/campagnes/:campaignId', element: <LegacyCampaignTabRedirect /> },
                                            { path: 'prospection/campagnes/:campaignId/:onglet', element: <S><CrmProspectingCampaign /></S> },
                                            { path: 'prospection/trames/:templateId', element: <S><CrmProspectingTemplate /></S> },
                                            { path: 'prospection/atelier/nouveau', element: <S><CrmProspectingWorkshopNew /></S> },
                                            { path: 'prospection/atelier/:sessionId', element: <S><CrmProspectingWorkshop /></S> },
                                            { path: 'prospection/reglages/boites', element: <S><CrmProspectingMailboxes /></S> },
                                            { path: 'prospection/reglages/import', element: <S><CrmProspectingImport /></S> },
                                            { path: 'prospection/reglages/bilan', element: <S><CrmProspectingRecapSettings /></S> },
                                            { path: 'prospection/boites', element: <RedirectKeepingQuery to={PROSPECTING_PATHS.mailboxes} /> },
                                            { path: 'prospection/import', element: <RedirectKeepingQuery to={PROSPECTING_PATHS.import} /> },
                                        ],
                                    },
                                    {
                                        path: 'analytics',
                                        element: <S><AnalyticsLayout /></S>,
                                        children: [
                                            { index: true, element: <S><AnalyticsGeneral /></S> },
                                        ],
                                    },
                                    // Statistiques IA : même URL, mais rangées dans la section IA.
                                    { path: 'analytics/ai', element: <S><AiStatsPage /></S> },
                                    {
                                        path: 'config',
                                        children: [
                                            { path: '', element: <Navigate to="roles" replace /> },
                                            { path: 'roles', element: <S><SAConfigRoles /></S> },
                                            { path: 'plugs', element: <S><SAConfigPlugs /></S> },
                                            { path: 'ai', element: <S><SAConfigAi /></S> },
                                            { path: 'ai/models', element: <S><AiModelsPage /></S> },
                                            { path: 'ai/quality', element: <S><AiQualityPage /></S> },
                                            { path: 'pms-connectivity', element: <S><SAConfigPmsConnectivity /></S> },
                                            { path: 'pms-connectivity/:slug', element: <S><SAConfigPmsDetail /></S> },
                                            { path: 'saml', element: <S><SAConfigSaml /></S> },
                                            { path: 'mjml-rules', element: <S><SAConfigMjmlRules /></S> },
                                            { path: 'email-types', element: <S><SAConfigAiEmailTypes /></S> },
                                        ],
                                    },
                                    {
                                        path: 'changelog',
                                        children: [
                                            { path: '', element: <S><SAChangelogIndex /></S> },
                                            { path: 'new', element: <S><SAChangelogForm /></S> },
                                            { path: ':id', element: <S><SAChangelogForm /></S> },
                                        ],
                                    },
                                    {
                                        path: 'seo',
                                        children: [
                                            { path: 'landing-backlinks', element: <S><SALandingBacklinksIndex /></S> },
                                        ],
                                    },
                                    {
                                        path: 'flow-templates',
                                        children: [
                                            { path: '', element: <S><SAFlowTemplatesIndex /></S> },
                                            { path: 'brouillons', element: <S><SAFlowTemplateDrafts /></S> },
                                        ],
                                    },
                                    {
                                        path: 'email-library',
                                        children: [
                                            { path: '', element: <S><SAEmailLibrary status="published" /></S> },
                                            { path: 'brouillons', element: <S><SAEmailLibrary status="draft" /></S> },
                                        ],
                                    },
                                    {
                                        path: 'survey-library',
                                        children: [
                                            { path: '', element: <S><SASurveyLibrary status="published" /></S> },
                                            { path: 'brouillons', element: <S><SASurveyLibrary status="draft" /></S> },
                                        ],
                                    },
                                    {
                                        path: 'flow-template-types',
                                        element: <S><SAFlowTemplateTypesIndex /></S>,
                                    },
                                    {
                                        path: 'email-styles',
                                        element: <S><SAEmailStyleMatrixIndex /></S>,
                                    },
                                    {
                                        path: 'ai-reference-emails',
                                        element: <S><SAAiReferenceEmailsIndex /></S>,
                                    },
                                    {
                                        path: 'flow-stats',
                                        element: <S><FlowStatsPage /></S>,
                                    },
                                    {
                                        path: 'housekeeping-messaging',
                                        element: <S><HkMessagingStatsPage /></S>,
                                    },
                                    {
                                        path: 'flow-stats/tagging',
                                        element: <S><FlowTaggingPage /></S>,
                                    },
                                    {
                                        path: 'flow-stats/health',
                                        element: <S><FlowHealthPage /></S>,
                                    },
                                    // Livres blancs : section INDÉPENDANTE de l'établissement
                                    // sélectionné. Volontairement hors de `super-admin/crm`,
                                    // dont le préfixe ouvre à `_crm:any` dans routePermissions,
                                    // alors que l'API est gardée par le middleware `super_admin`.
                                    {
                                        path: 'lead-magnets',
                                        element: <S><LeadMagnetsPage /></S>,
                                    },
                                    {
                                        path: 'lead-magnets/:slug',
                                        element: <S><LeadMagnetDetailPage /></S>,
                                    },
                                    // Relecture du blog : même logique que les livres blancs, hors
                                    // `super-admin/crm` (l'API est gardée par `super_admin`).
                                    {
                                        path: 'blog',
                                        element: <S><BlogReviewsPage /></S>,
                                    },
                                    {
                                        path: 'blog/:id',
                                        element: <S><BlogReviewDetailPage /></S>,
                                    },
                                    // Assistant support : API gardée par `super_admin`, hors `super-admin/crm`.
                                    {
                                        path: 'support',
                                        element: <S><SupportInboxPage /></S>,
                                    },
                                    {
                                        path: 'support/unanswered',
                                        element: <S><SupportUnansweredPage /></S>,
                                    },
                                    {
                                        path: 'support/stats',
                                        element: <S><SupportStatsPage /></S>,
                                    },
                                    {
                                        path: 'support/settings',
                                        element: <S><SupportSettingsPage /></S>,
                                    },
                                    {
                                        path: 'support/models',
                                        element: <S><SupportModelsPage /></S>,
                                    },
                                    {
                                        path: 'support/manual',
                                        element: <S><SupportManualPage /></S>,
                                    },
                                    {
                                        path: 'support/manual/:id',
                                        element: <S><SupportManualProposalPage /></S>,
                                    },
                                    {
                                        path: 'help-articles',
                                        element: <S><HelpArticlesPage /></S>,
                                    },
                                    {
                                        path: 'help-categories',
                                        element: <S><HelpCategoriesPage /></S>,
                                    },
                                    {
                                        path: 'help-articles/:id',
                                        element: <S><HelpArticleEditPage /></S>,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },

    // Routes Auth (guest)
    {
        path: '/',
        element: <GuestLayout/>,
        children: [
            { path: 'login', element: <S><Login /></S> },
            { path: 'login/:slug', element: <S><Login /></S> },
            { path: 'signup', element: <S><Signup /></S> },
            { path: 'signup/:slug', element: <S><Signup /></S> },
            { path: 'forgot-password', element: <S><ForgotPassword /></S> },
            { path: 'forgot-password/:slug', element: <S><ForgotPassword /></S> },
            { path: 'reset-password/:token', element: <S><ResetPassword /></S> },
            { path: 'reset-password/:slug/:token', element: <S><ResetPassword /></S> },
        ],
    },

    // Routes publiques
    {
        path: '/',
        element: <PublicLayout/>,
        children: [
            { path: 'opt-out/:facilityId', element: <S><OptOut /></S> },
            { path: 'opt-out-phone/:facilityId', element: <S><OptOutPhone /></S> },
            {
                path: 'survey-response',
                children: [
                    { path: ':surveySlug', element: <S><SurveyResponses /></S> },
                    { path: ':surveySlug/:actionLaunchReservationSlug', element: <S><SurveyResponses /></S> },
                ],
            },
            { path: 'locks/:facilitySlug/:actionLaunchReservationSlug', element: <S><PublicReservationLocks /></S> },
            { path: 'calendar/:facilitySlug/:actionLaunchReservationSlug', element: <S><PublicReservationCalendar /></S> },
            { path: 'calendar-custom/:facilitySlug/:token', element: <S><PublicCustomCalendar /></S> },
            { path: 'public-sales-view/:token', element: <S><PublicSalesView /></S> },
            { path: 'public-booking-request/:token', element: <S><PublicBookingRequest /></S> },
            { path: 'public-occupants-view/:token', element: <S><PublicOccupantsView /></S> },
            { path: 'salon/:token', element: <S><PublicSalon /></S> },
            { path: 'salon-guides/:token', element: <S><PublicSalonFlyer /></S> },
            { path: 'salon-equipe/:token', element: <S><PublicSalonTeam /></S> },
            { path: 'salon-diagnostic/:token', element: <S><PublicSalonDiagnostic /></S> },
            { path: 'salon-prestataire/:token', element: <S><PublicSalonPartner /></S> },
            { path: 'webinaire/:token', element: <S><PublicWebinar /></S> },
            { path: 'formulaire/:token', element: <S><PublicWebinar /></S> },
            { path: 'wallet/:facilitySlug/:actionLaunchReservationSlug', element: <S><PublicReservationWallet /></S> },
            { path: 'scan/:facilitySlug/:reservationSlug', element: <S><ReservationScanPage /></S> },
            { path: 'kiosk/:facilitySlug', element: <S><PublicCheckinKiosk /></S> },
            { path: 'kiosk/:facilitySlug/:surveySlug', element: <S><PublicCheckinKiosk /></S> },
            {
                path: 'payment',
                children: [
                    { path: ':slug', element: <S><PublicPaymentPage /></S> },
                    { path: ':slug/:actionSlug', element: <S><PublicPaymentPage /></S> },
                ],
            },
        ],
    },

    // SSO SAML — retour d'authentification (hors layout : pages transitoires)
    {
        path: '/sso/callback',
        element: <S><SsoCallback /></S>,
    },
    {
        path: '/sso/error',
        element: <S><SsoError /></S>,
    },

    // Standalone Chat
    {
        path: '/chat/:token',
        element: <S><StandaloneChatPage /></S>,
    },

    // 404
    {
        path: '*',
        element: <NotFound/>
    },
]);

export default router;
